import axios, { AxiosResponse } from "axios";
import React from "react";

import {
  APIKey,
    FormSectionElements,
    FormSections,
    FormSubSections,
    LOVValues,
    ProgramContainer,
    SummaryStyleType
} from "../../entities/formTemplateDataType";
import Card from "../card";
import {InfoElementComponent} from "./InfoElementComponent";
import FormValidationUtils from "../../utilities/FormValidationUtils";
import StepIndicator from "../subComponent/step-indicator/StepIndicator";
import {CheckboxComponent} from "../subComponent/CheckboxComponent";
import {MultipleCheckboxComponent} from "../subComponent/MultipleCheckboxComponent";
import {RichTextComponent} from "../subComponent/RichTextComponent";
import {SpinnerComponent} from "../../commonComponent/SpinnerComponent";
import {ErrorAlertsComponent} from "../odsCalloutBox/ErrorAlertsComponent";
import DynamicComponentRenderer from "../../DynamicComponentRenderer";
import {SubSectionComponent} from "../SubSectionComponent";
import {TableMenuComponent} from "../subComponent/TableMenuComponent";
import {SummaryComponent} from "../subComponent/SummaryComponent";
import {USER_PROFILE} from "../../entities/Constants";
import TableSummaryComponent from "./TableSummaryComponent";
import SummaryListComponent from "../subComponent/SummaryListComponent";
import PagedSummaryListComponent from "../subComponent/PagedSummaryListComponent";
import Utils from "../../utilities/Utils";
import {styled} from "styled-components";
import {colour, Heading4, spacing} from "../../theme";
import {findSectionElementByFieldName, getCustomizedSummaryComponentStep, getRootComponent} from "../../utilities/ElementUtils";
import {ENGLISH_SLOGAN, GRAPHIC, NO_GRAPHIC, renderPlateImage} from "../../utilities/custom/PlpUtils";
import EditSubsectionSummaryComponent from "./EditSubsectionSummaryComponent";
import {RadioButtonComponent} from "../subComponent/RadioButtonComponent";
import {InformationalAlertComponent} from "../odsCalloutBox/InformationalAlertComponent";
import {SuccessAlertComponent} from "../odsCalloutBox/SuccessAlertComponent";
import {WarningAlertComponent} from "../odsCalloutBox/WarningAlertComponent";
import {ErrorAlertComponent} from "../odsCalloutBox/ErrorAlertComponent";
import FadeableWrapper from "../odsCalloutBox/FadeableWrapper";
import {TextComponent} from "../subComponent/TextComponent";
import {MultiTextComponent} from "../subComponent/MultiTextComponent";
import {DateComponent} from "../subComponent/DateComponent";
import {PhoneComponent} from "../subComponent/PhoneComponent";
import {EmailComponent} from "../subComponent/EmailComponent";
import {NumberComponent} from "../subComponent/NumberComponent";
import {DropdownComponent} from "../subComponent/DropdownComponent";
import IncidentReportConfirmationComponent from "../subComponent/IncidentReportConfirmationComponent";
import { combineUrl, resolveApiUrl } from "../../utilities/URLUtils";
import { DynamicUrlInterpreter } from "../../utilities/DynamicUrlInterpreter";
import SessionStorageService from "../../services/SessionStorageService";
import { getEnvVar, SCRIPT_ENV } from "../../config/RuntimeConfig";
import { setBaseHeaders } from "../../utilities/submitUtils";
import DataUtils from "../../utilities/DataUtils";
import { formatField } from "../../utilities/PatternFormatterUtils";
import { generateAndDownloadPDF } from "../../services/PDFGenerationService";
import { se } from "date-fns/locale";
import FieldModalComponent from "../subComponent/FieldModalComponent";

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  z-index: 999;
`;

const ModalBackground = styled.div`
position: fixed;
top: 50%;
left: 50%;
width: 468px;
height: auto;
margin: 0;
transform: translate(-50%, -50%);
background: ${colour.palette.white};
overflow: auto;
outline: transparent solid 4px;
padding: ${spacing.spacing5} ${spacing.spacing4};
box-shadow: rgba(26, 26, 26, 0.6) 0 0 2px;
transition: box-shadow 0.1s ease-in-out 0s;
z-index: 1000;
`;

export const ModalBody = styled.div`
    margin-top: ${spacing.spacing4};

    p {
        line-height: 35px;
    }
`;

export const ModalFooter = styled.div`
    margin-top: ${spacing.spacing8};
`;

interface Props {
  programContainer: ProgramContainer;
  isEng: boolean;
  activesteps: Set<number>;
  style?: SummaryStyleType;
  callback: (programContainer: ProgramContainer) => void;
  callbackStepIndex: (index: number) => void;
  confirmationVisible: boolean;
  summaryVisible: boolean;
  setStateProp: (confirmationAvailable: boolean, 
      summaryVisible: boolean, 
      transactionId:string,
      submissionError:boolean, 
      showDownloadSummary: boolean, 
      applicationType: string, 
      renewType: string )=>void;
  transactionId: string;
  submissionError: boolean;
  showDownloadSummary: boolean;
  applicationType: string;
  renewType?: string;
  loginToSaveButton?: React.ReactNode;
  currentCustomizedReviewSection?: FormSections;
}

interface State {
  isBusy: boolean;
  validationErrMsg: any;
  token: string;
  uploadReceiptSuccess: boolean;
  removePlatePopUp: number;
  confirmationVisible: boolean;
  summaryVisible?: boolean;
  renewalResponse?: any;
  captchaToken?: string;
  pendingApiAction?: {
    method: 'get' | 'post' | 'put' | 'delete' | 'patch';
    url: string;
    headers: any;
    field: any;
  } | null;
  successBannerMessage?: string | null;
}

export class CustomizedSummaryComponent extends React.Component<Props, State> {
  HtmlToReactParser = require('html-to-react').Parser;

  private getValueByPath(source: any, path: string): any {
    if (!source || !path) return undefined;

    const segments = path.split('.').filter(Boolean);
    let current = source;

    for (const segment of segments) {
      if (current === null || current === undefined || typeof current !== 'object') {
        return undefined;
      }

      const direct = current[segment];
      if (direct !== undefined) {
        current = direct;
        continue;
      }

      const matchedKey = Object.keys(current).find(key => key.toLowerCase() === segment.toLowerCase());
      if (!matchedKey) {
        return undefined;
      }

      current = current[matchedKey];
    }

    return current;
  }

  private resolveTextPlaceholders(text: string | undefined, programContainer: ProgramContainer): string {
    if (!text) return '';

    return text.replace(/\{\{([^}]+)\}\}/g, (_match, rawKey) => {
      const key = String(rawKey || '').trim();
      const valueFromEntity = this.getValueByPath(programContainer?.Entity, key);
      if (valueFromEntity !== undefined && valueFromEntity !== null && valueFromEntity !== '') {
        return String(valueFromEntity);
      }
      return `{{${key}}}`;
    });
  }

  constructor(props: Props) {
    super(props);

    this.state = {
      isBusy: false,
      validationErrMsg: [],
      token: null,
      uploadReceiptSuccess: null,
      removePlatePopUp: -1,
      pendingApiAction: null,
      successBannerMessage: null,
      confirmationVisible: this.props.confirmationVisible,
      summaryVisible: this.props.summaryVisible,
      renewalResponse: null
    }
  }

  async componentDidMount() {
    console.log("componentDidMount")
    window.scrollTo({ top: 0, behavior: 'smooth' });
    
    const field = this.props.programContainer.ListOfFormSections.filter(p => p.CustomizedReviewPage)?.[0];
    if (field.GetAPI) {
      let api = resolveApiUrl(field.GetAPI);

      const programContainer = getRootComponent(field);
      const isExternalUrl = api.startsWith("http");
      if (!isExternalUrl) {
          api = programContainer.APIBasePath?.replace(/\/+$/, '') + '/' + api.replace(/^\/+/, '');
      }

      const urlWithoutProtocol = api.replace(/^https?:\/\//, '');
      if (urlWithoutProtocol.includes('//')) {
          console.log('[PagedSummaryList] Skipping request with unresolved placeholder:', api);
          return;
      }
      let response: AxiosResponse<any, any>;
      const apiKey: APIKey = (programContainer.ApiKeyKey && programContainer.ApiKeyValue)
          ? { key: programContainer.ApiKeyKey, value: programContainer.ApiKeyValue }
          : null;
        const token = !isExternalUrl
          ? (SessionStorageService.getAuthToken(programContainer.AuthRequired) ?? programContainer.Profile?.token)
          : null;
      const headers = setBaseHeaders('application/json', token, apiKey);
      const configs = isExternalUrl
          ? { headers: { 'Content-Type': 'application/json' } }
          : { headers, withCredentials: true };

      const fullUrl = combineUrl(programContainer.APIBasePath, api);
      try {
        response = await axios.get(fullUrl, configs);
      } catch (ex) {
        console.log(ex);
        return;
      } finally {
      }
      if (!response) {
        return;
      }

      programContainer.Entity = response.data;
      DataUtils.assignFormValue(programContainer);
      this.props.callback(programContainer);
    }  
  }

  handleEditButtonClick = async (field: FormSectionElements) => {
    if (field.Script) {
      if (field.Datatype == "Button") {
        console.log("Script to eval:", field.Script);
        try {
          const customJSFunc = eval(`(${field.Script})`);
          const programContainer = getRootComponent(field) as ProgramContainer;
          const navigateToStep = (pageToGoTo?: number | string) => {
            if (typeof pageToGoTo === "number") {
              this.props.callbackStepIndex(Math.max(0, pageToGoTo - 1));
              return;
            }

            if (typeof pageToGoTo === "string") {
              const foundSection = programContainer.ListOfFormSections.find(
                section => section.ENUTitle === pageToGoTo || section.FRATitle === pageToGoTo
              );
              if (!foundSection) return;
              const visibleSections = programContainer.ListOfFormSections.filter(section => section.Visible !== false);
              const indexNum = visibleSections.indexOf(foundSection);
              if (indexNum >= 0) {
                this.props.callbackStepIndex(indexNum);
              }
            }
          };

          if (typeof customJSFunc === "function") {
            const functionContext = { generateAndDownloadPDF, programContainer, callbackStepIndex: navigateToStep };
            try {
              const refresh = customJSFunc(field, SCRIPT_ENV, functionContext);
              if (typeof refresh === 'boolean' && refresh) {
                 this.props.callback(getRootComponent(field));
              }
            } catch (innerError) {
              const innerMessage = innerError instanceof Error ? innerError.message : String(innerError);
              const isLegacyWrapper = /is not a function/i.test(innerMessage) && customJSFunc.length <= 1;
              if (!isLegacyWrapper) {
                throw innerError;
              }

              customJSFunc(navigateToStep);
            }
          }
        // Store this button as the trigger on the modal field, so the modal knows which API to call
        const modalField = (field.parent as FormSubSections)?.ListOfFormSectionElements?.find(el => el.Datatype === 'Modal');
        if (modalField) (modalField as any)._triggerButton = field;

        } catch (error) {
          console.error("Error executing field.Script", error);
        }
      } else {
        FormValidationUtils.runJavascript(field);
      }
    }
    if (field.StepperScript) {
      try {
        const customJSFunc = eval(`(${field.StepperScript})`);
        if (typeof customJSFunc === "function") {
          customJSFunc((index) => { this.props.callbackStepIndex(index) });
        }
      } catch (error) {
        console.error("Error executing field.StepperScript", error);
      }
    }
    // Handle API calls for buttons (e.g., download)
    // Skip if field.Script is set — the modal's approve button will call the API instead
    if (field.Api && field.Datatype == "Button" && !field.StepperScript && !field.Script) {
        await this.callFieldApi(field);
    }
  }

  callFieldApi = async (field: FormSectionElements, data?: any) => {
    const programContainer = getRootComponent(field) as ProgramContainer;
    let api = resolveApiUrl(field.Api);

    // Resolve dynamic URL placeholders
    api = DynamicUrlInterpreter.interpretUrl(api, {
      programContainer,
      appName: programContainer.CategoryId
    });

    const token = SessionStorageService.getAuthToken(programContainer.AuthRequired) ?? programContainer.Profile?.token;
    const apiKey: APIKey = (field.ApiKeyKey && field.ApiKeyValue)
      ? { key: field.ApiKeyKey, value: field.ApiKeyValue }
      : null;
    const headers = setBaseHeaders('application/json', token, apiKey);

    try {
      const method = ((field as any).SubmissionMethod || field.SubmitButton?.SubmissionMethod || 'GET').toUpperCase();
      if (method === 'GET') {
        // For GET requests, trigger download
        const response = await fetch(api, { method: 'GET', headers });
        if (!response.ok) throw new Error('Status: ' + response.status);
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        // Extract filename from Content-Disposition header or use default
        const contentDisposition = response.headers.get('Content-Disposition');
        let filename = 'download';
        if (contentDisposition) {
          const match = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
          if (match) filename = match[1].replace(/['"]/g, '');
        }
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      } else {
        // For other methods, make regular axios call
        await axios({ method, url: api, headers, ...(data && { data }) });
      }
      return true;
    } catch (error) {
      console.error('API call failed:', error);
      alert('Operation failed: ' + (error as Error).message);
      return false;
    }
  }

  handleSubsectionEditButtonClick = (subSection: FormSubSections) => {
    if (!subSection) return;

    // First, honor a subsection-level StepperScript to jump to a specific step.
    if (subSection.StepperScript) {
      try {
        const customJSFunc = eval(`(${subSection.StepperScript})`);
        customJSFunc((pageToGoTo?: number | string) => {
          if (typeof pageToGoTo === "number") {
            const targetIndex = Math.max(0, pageToGoTo - 1);
            this.props.callbackStepIndex(targetIndex);
          } else if (typeof pageToGoTo === "string") {
            const root = getRootComponent(subSection);
            const foundSection = root.ListOfFormSections.find(section => section.ENUTitle == pageToGoTo);
            const indexNum = root.ListOfFormSections.filter(p => p.Visible).indexOf(foundSection);
            this.props.callbackStepIndex(indexNum)
          }
        }, subSection, this.props.programContainer, SCRIPT_ENV);
        return;
      } catch (error) {
        console.error("Error executing subsection StepperScript", error);
      }
    }

    // Fallback to legacy change-link script if provided on the parent section.
    if (subSection.parent.CustomizedSummaryProps?.changeLinkScript) {
      const customJSFunc = eval(`(${subSection.parent.CustomizedSummaryProps?.changeLinkScript})`);
      this.props.callbackStepIndex(customJSFunc(subSection));
    }
  }

  generateSummaryHtmlString = () => {
    if (!this.state.summaryVisible) return null;
    let SectionHtmlString = [];
    let sumStyle = '';

    const programContainer = this.props.programContainer;
    // When a specific section is provided, scope to just that section.
    let sectionList = programContainer.ListOfFormSections.filter(
      p => p.CustomizedReviewPage && !p.ExcludeFromCustomizedSummary
    );
    if (this.props.currentCustomizedReviewSection) {
      sectionList = sectionList.filter(s => s === this.props.currentCustomizedReviewSection);
    }

    // const stepNumer = Array.from(this.props.activesteps)[this.props.activesteps.size - 1];
    const stepNumer = getCustomizedSummaryComponentStep(this.props.programContainer);
   
    if (stepNumer !== null && stepNumer !== undefined) {
      SectionHtmlString.push(
        <StepIndicator withbackbtn={true} title="Back" sectionSequence={stepNumer} isEng={this.props.isEng}
          section={sectionList[0]}
          callback={currentStep => { this.props.callbackStepIndex(currentStep) }}>
        </StepIndicator>
      );
    }

    SectionHtmlString.push(
      <ErrorAlertsComponent errorMsgList={this.state.validationErrMsg} isEng={this.props.isEng} ></ErrorAlertsComponent>
    )

    for (const section of sectionList) {
      // Ensure parent references are set (they are not serialized in JSON)
      section.parent = programContainer;
      section.ListOfFormSubSections?.forEach(sub => {
        sub.parent = section;
        sub.ListOfFormSectionElements?.forEach(el => { el.parent = sub; });
      });

      if (this.props.style === 'Default') {
        if (programContainer.CustomSummaryHeader) {
          const htmlToReactParser = new this.HtmlToReactParser();
          SectionHtmlString.push(htmlToReactParser.parse(this.props.programContainer.CustomSummaryHeader));
        }
        console.log('custom default')
        SectionHtmlString.push(
        <SummaryComponent 
          key={'SummaryComponent'} 
          programContainer={programContainer} 
          isEng={this.props.isEng}/>);
      } else {
        const sectionTitle = this.resolveTextPlaceholders(this.props.isEng ? section.ENUTitle : section.FRATitle, programContainer);
        const sectionDescription = this.resolveTextPlaceholders(this.props.isEng ? section.ENUDescription : section.FRADescription, programContainer);
        SectionHtmlString.push(
          <div>
            {sectionTitle &&
              (
                <h1 className="ontario-margin-bottom-24-!">{sectionTitle}</h1>
              )
            }
            {sectionDescription &&
              (
                <div
                  className="ontario-margin-bottom-32-!">{sectionDescription}</div>
              )
            }
          </div>
        );
      }

      if (section.PreScript) {
        try {
          const customJSFunc = eval(`(${section.PreScript})`);
          customJSFunc(section);
        } catch (ex) {
          console.log(ex);
        }

      }

      if (section?.followSubSectionOrder && section?.CustomizedReviewPage && section?.ListOfFormSubSections?.length > 0) {
        const groupConsecutiveByNotInCardReduce = (array) => {
          return array?.reduce((acc, current, index) => {
            if (index === 0 || current?.NoCardInSummary !== array[index - 1]?.NoCardInSummary) {
              acc.push({
                id: acc.length + 1,
                NoCardInSummary: !!current?.NoCardInSummary,
                list: [current]
              });
            } else {
              acc[acc.length - 1]?.list?.push(current);
            }
            return acc;
          }, []);
        };

        const groupedSubSections = groupConsecutiveByNotInCardReduce(section?.ListOfFormSubSections)

        groupedSubSections?.forEach((group) => {
          if (group?.NoCardInSummary) {
            const filteredList = group?.list?.filter(p => p.NoCardInSummary && p.Visible != false);
            filteredList?.forEach((subSection) => {
              this.processSubsectionListNotInCard(subSection, SectionHtmlString);
            });
          } else {
            let filteredList = group?.list?.filter(p => p.NoCardInSummary !== true && p.Visible != false);
            let numberOfDuplicates = this.processDuplicates(filteredList, programContainer);
            let duplicateSubsecIndex = 0;

            filteredList?.forEach((subSection) => {
              this.processSubsectionListInCard(subSection, duplicateSubsecIndex, programContainer, sumStyle, SectionHtmlString, numberOfDuplicates);
              duplicateSubsecIndex++;
            });
          }
        });
      } else {
        section.ListOfFormSubSections.forEach((ss) => {  // Trigger pre-script before the rest of the process.
          try {
            if (ss.PreScript?.trim().length > 0) {
              const customJSFunc = eval(`(${ss.PreScript})`);
              customJSFunc(ss, SCRIPT_ENV);
            }
          } catch (ex) {
              console.log(ex);
          }
        })

        let subsectionListInCard = section.ListOfFormSubSections.filter(p => p.NoCardInSummary !== true && p.Visible != false);
        let numberOfDuplicates = this.processDuplicates(subsectionListInCard, programContainer);  // Inserting duplicate sub-sections with no value
        let duplicateSubsecIndex = 0;
        for (const subSection of subsectionListInCard) {
          this.processSubsectionListInCard(subSection, duplicateSubsecIndex, programContainer, sumStyle, SectionHtmlString, numberOfDuplicates);
          if (Utils.isNotNullOrEmpty(subSection.SummarizeDuplicateSubsection)) {
            duplicateSubsecIndex++;  // This index is only being used for duplicate sub-sections.
          }
        }

        const subsectionListNotInCard = section.ListOfFormSubSections.filter(p => p.NoCardInSummary && p.Visible != false);

        for (const subSection of subsectionListNotInCard) {
          this.processSubsectionListNotInCard(subSection, SectionHtmlString);
        }
      }

    }

    const currentSection = sectionList[0];
    if (!currentSection?.NoDefaultContinue) {
      let submitComponentName = "Submit";
      if (this.props.programContainer.CategoryId.toUpperCase() == "CHC") {
        submitComponentName = "CHCSubmit";
      }
      if (this.props.programContainer.CategoryId.toUpperCase() === "ODR") {
        submitComponentName = "ODRSubmit";
      }
      else if (this.props.programContainer.CategoryId.toUpperCase() == "APP") {
        submitComponentName = "APPSubmit"
      }
      else if (this.props.programContainer.CategoryId.toUpperCase() === "VECON") {
        submitComponentName = "VECONSubmit";
      }
      else if (this.props.programContainer.CategoryId.toLowerCase() === "library-publication-dev") { //temporary, will be changed
        submitComponentName = "LibraryPublicationSubmit";
      }

      const props = {
        programContainer: this.props.programContainer,
        isEng: this.props.isEng,
        section: currentSection,
        sumStyle: this.props.style
      };

      const submitButton = ( 
        <DynamicComponentRenderer componentName={submitComponentName} componentProps={props} callbackStepIndex={(index) => {
            console.log(index);
            this.props.callbackStepIndex(index);
          }}
          onButtonClick={(props) => {
            this.props.setStateProp(props.confirmationVisible, props.summaryVisible, props.transactionId,props.submissionError, props.showDownloadSummary, props.applicationType, props.renewType );
            this.setState({
              isBusy: props.isBusy,
              uploadReceiptSuccess: props.uploadReceiptSuccess,
              summaryVisible: props.summaryVisible,
              confirmationVisible: props.confirmationVisible,
              validationErrMsg: props.validationErrMsg,
              renewalResponse: props.renewalResponse,
              captchaToken: props.captchaToken
            })
          }}
          callback={(programContainer) => this.props.callback(programContainer)}
      />
      );
      
      // To move the position of 'Submit' button to left & renaming the label as 'Continue to Payment':
      // Find the section (within CustomizedReviewPage sections) where 'KeepContinueButtonLeft' is true and 'NextButtonLabel' field in form builder is set to as 'Continue to payment'.
      // **If this condition needs to change in the future, update the logic below or add an additional attribute check.
      const section = programContainer.ListOfFormSections.filter(p => p.CustomizedReviewPage)?.[0];
      const justifyContentStr = section?.KeepContinueButtonLeft || section?.NextButtonLabel === 'Continue to payment' ? 'flex-start': 'flex-end';
      const buttonTopGap = section?.CustomizedSummaryProps?.buttonTopGap?.trim() || '1rem';
      SectionHtmlString.push(
        <div key="button-container" className="form-action-buttons" style={{ display: 'flex', justifyContent: justifyContentStr, alignItems: 'flex-start', gap: '1rem', marginTop: buttonTopGap, width: '100%' }}>
        {this.props.loginToSaveButton}
          {submitButton}
        </div>
      );
    }

    return SectionHtmlString;
  }

  private processSubsectionListNotInCard(subSection: FormSubSections, SectionHtmlString: any[]) {
    subSection.ListOfFormSectionElements.sort((a, b) => a.Sequence - b.Sequence).forEach((element: FormSectionElements) => {
      const HtmlToReactParser = require('html-to-react').Parser;
      const htmlToReactParser = new HtmlToReactParser();
      switch (element.Datatype) {
        case "TableMenu":
          SectionHtmlString.push(
            <TableMenuComponent
              field={element}
              isEng={this.props.isEng}
              callbackStepIndex={(index, returnIndex) => this.props.callbackStepIndex(index)}
              callbackElement={(container) => {
                this.props.callback(container)
              }}>
            </TableMenuComponent>
          );
          break;
        case "Checkbox":
          SectionHtmlString.push(
            <CheckboxComponent field={element}
              editable={true}
              title={this.props.isEng ? element.ENUTitle : element.FRATitle}
              labelControlArray={this.props.isEng ? (element.richTextChecked ? htmlToReactParser.parse(element.ENUTitle) : element.ENUTitle) : (element.richTextChecked ? htmlToReactParser.parse(element.FRATitle) : element.FRATitle)}
              requiredField={true}
              callbackElement={(container) => {
                this.props.callback(container);
              }}
              isEng={this.props.isEng}
              usedForFilter={false}
              requiredLabel={false}
              hint={null}></CheckboxComponent>
          )
          break;

        case "CheckGroup":
          SectionHtmlString.push(
            <MultipleCheckboxComponent key={"MultipleCheckboxComponent" + element.LoadBindName}
              field={element}
              editable={true}
              title={this.props.isEng ? element.ENUTitle : element.FRATitle}
              labelControlArray={this.props.isEng ? (element.richTextChecked ? htmlToReactParser.parse(element.ENUTitle) : element.ENUTitle) : (element.richTextChecked ? htmlToReactParser.parse(element.FRATitle) : element.FRATitle)}
              requiredField={true}
              isEng={this.props.isEng}
              callbackElement={(container) => {
                this.props.callback(container);
              }}>
            </MultipleCheckboxComponent>
          )
          break;
          
        case 'RadioButton':
          SectionHtmlString.push(
              <RadioButtonComponent key={"RadioButtonComponent_" + element.parent.Id + "_" + element.Id}
              field={element}
              editable={true} 
              title={this.props.isEng ? element.ENUTitle : element.FRATitle} 
              labelControlArray={this.props.isEng ? element.ENUTitle : element.FRATitle}
              requiredField={true} 
              Visible={element.Visible}
              isEng={this.props.isEng}
              callbackElement={(container) => {
                this.props.callback(container);
              }}
            />) 
          break;

        case "Label":
          SectionHtmlString.push(
            <RichTextComponent
              field={element}
              editable={true}
              title={this.props.isEng ? element.ENUTitle : element.FRATitle}
              labelControlArray={this.props.isEng ? element.ENUTitle : element.FRATitle}
              requiredField={false}
              callbackElement={null}
              callbackStepIndex={(index) => {
                  this.props.callbackStepIndex(index)
              }}
              isEng={this.props.isEng}
            >
            </RichTextComponent>
          )
          break;
        case "InformationAlert":
          SectionHtmlString.push(<InformationalAlertComponent  isEng={this.props.isEng} field={element}/>);
          break;
        case "SuccessAlert": {
          const bodyMsgS = this.props.isEng ? element.callOutBoxBody : element.callOutBoxBodyFRA;
          SectionHtmlString.push(
            <FadeableWrapper key={`success_alert_${element.Id}`} field={element} onFadeComplete={() => this.props.callback(getRootComponent(element))}>
              <SuccessAlertComponent
                field={element}
                isEng={this.props.isEng}
                Visible={element.Visible !== false}
                bodyMsg={bodyMsgS}
                title={this.props.isEng ? element.MessageTitle : element.MessageTitleFRA}
              />
            </FadeableWrapper>
          );
          break;
        }
        case "ErrorAlert": {
          const bodyMsgE = this.props.isEng ? element.callOutBoxBody : element.callOutBoxBodyFRA;
          SectionHtmlString.push(
            <FadeableWrapper key={`error_alert_${element.Id}`} field={element} onFadeComplete={() => this.props.callback(getRootComponent(element))}>
              <ErrorAlertComponent
                field={element}
                isEng={this.props.isEng}
                Visible={element.Visible !== false}
                bodyMsg={bodyMsgE}
                title={this.props.isEng ? element.MessageTitle : element.MessageTitleFRA}
              />
            </FadeableWrapper>
          );
          break;
        }
        case "WarningAlert": {
          const bodyMsgW = this.props.isEng ? element.callOutBoxBody : element.callOutBoxBodyFRA;
          SectionHtmlString.push(
            <FadeableWrapper key={`warning_alert_${element.Id}`} field={element} onFadeComplete={() => this.props.callback(getRootComponent(element))}>
              <WarningAlertComponent
                isEng={this.props.isEng}
                Visible={element.Visible !== false}
                bodyMsg={bodyMsgW}
                title={this.props.isEng ? element.MessageTitle : element.MessageTitleFRA}
              />
            </FadeableWrapper>
          );
          break;
        }
        case "SummaryList":
          SectionHtmlString.push(
            <SummaryListComponent
              key={`summary_list_${element.Id}`}
              field={element}
              isEng={this.props.isEng}
              callbackStepIndex={(index, returnIndex) => this.props.callbackStepIndex(index)}
              callback={(programContainer) => this.props.callback(programContainer)}
            />
          );
          break;
        case "PagedSummaryList":
          SectionHtmlString.push(
            <PagedSummaryListComponent
              key={`paged_summary_list_${element.Id}`}
              field={element}
              isEng={this.props.isEng}
              callbackStepIndex={(index, returnIndex) => this.props.callbackStepIndex(index)}
              callback={(programContainer) => this.props.callback(programContainer)}
            />
          );
          break;
        case "IncidentReportConfirmation":
          // Ensure element is visible for confirmation callout
          element.Visible = true;
          element.InvisibleByDefault = false;
          SectionHtmlString.push(
            <IncidentReportConfirmationComponent
              key={`incident_report_confirmation_${element.Id}`}
              field={element}
              programContainer={this.props.programContainer}
              isEng={this.props.isEng}
              callbackElement={(container) => this.props.callback(container)}
            />
          );
          break;
        case "Button":
          FormValidationUtils.runInitJavascript(element);
          if (element.Visible === false) {
            break;
          }
          if (element.StepperScript || element.Script) {
            let arrayToString = (value: string[]) => Array.isArray(value)
              ? value.join(' ') : (value ? String(value) : null);
            let btnClassName = "ontario-button " + (arrayToString(element.ClassName) ?? "ontario-button--secondary");
            SectionHtmlString.push(
              <button
                className={btnClassName}
                key={"btn_nocard_" + element.Id}
                onClick={() => this.handleEditButtonClick(element)}
              >
                {this.props.isEng ? element.ENUTitle : element.FRATitle}
              </button>
            );
          }
          break;

        case "Text":
          FormValidationUtils.runJavascript(element);
          if (element.Visible === false) break;
          SectionHtmlString.push(
            <TextComponent key={"TextComponent_nocard_" + element.Id}
              field={element} editable={true}
              title={this.props.isEng ? element.ENUTitle : element.FRATitle}
              labelControlArray={this.props.isEng ? element.ENUTitle : element.FRATitle}
              requiredField={element.Optional !== "Y"}
              isEng={this.props.isEng}
              callbackElement={(container) => { this.props.callback(container); }}
              callbackStepIndex={(index) => { this.props.callbackStepIndex(index); }} />
          );
          break;

        case "MultiText":
          FormValidationUtils.runJavascript(element);
          if (element.Visible === false) break;
          SectionHtmlString.push(
            <MultiTextComponent key={"MultiTextComponent_nocard_" + element.Id}
              field={element} editable={true}
              title={this.props.isEng ? element.ENUTitle : element.FRATitle}
              labelControlArray={this.props.isEng ? element.ENUTitle : element.FRATitle}
              requiredField={element.Optional !== "Y"}
              isEng={this.props.isEng}
              callbackElement={(container) => { this.props.callback(container); }} />
          );
          break;

        case "Date":
          FormValidationUtils.runJavascript(element);
          if (element.Visible === false) break;
          SectionHtmlString.push(
            <DateComponent key={"DateComponent_nocard_" + element.Id}
              field={element} editable={true}
              title={this.props.isEng ? element.ENUTitle : element.FRATitle}
              labelControlArray={this.props.isEng ? element.ENUTitle : element.FRATitle}
              requiredField={element.Optional !== "Y"}
              isEng={this.props.isEng}
              callbackElement={(container) => { this.props.callback(container); }} />
          );
          break;

        case "PhoneNumber":
          FormValidationUtils.runJavascript(element);
          if (element.Visible === false) break;
          SectionHtmlString.push(
            <PhoneComponent key={"PhoneComponent_nocard_" + element.Id}
              field={element} editable={true}
              title={this.props.isEng ? element.ENUTitle : element.FRATitle}
              labelControlArray={this.props.isEng ? element.ENUTitle : element.FRATitle}
              requiredField={element.Optional !== "Y"}
              isEng={this.props.isEng}
              callbackElement={(container) => { this.props.callback(container); }} />
          );
          break;

        case "Email":
          FormValidationUtils.runJavascript(element);
          if (element.Visible === false) break;
          SectionHtmlString.push(
            <EmailComponent key={"EmailComponent_nocard_" + element.Id}
              field={element} editable={true}
              title={this.props.isEng ? element.ENUTitle : element.FRATitle}
              labelControlArray={this.props.isEng ? element.ENUTitle : element.FRATitle}
              requiredField={element.Optional !== "Y"}
              isEng={this.props.isEng}
              callbackElement={(container) => { this.props.callback(container); }} />
          );
          break;

        case "Number":
          FormValidationUtils.runJavascript(element);
          if (element.Visible === false) break;
          SectionHtmlString.push(
            <NumberComponent key={"NumberComponent_nocard_" + element.Id}
              field={element} editable={true}
              title={this.props.isEng ? element.ENUTitle : element.FRATitle}
              labelControlArray={this.props.isEng ? element.ENUTitle : element.FRATitle}
              requiredField={element.Optional !== "Y"}
              isEng={this.props.isEng}
              callbackElement={(container) => { this.props.callback(container); }} />
          );
          break;

        case "Dropdown":
          FormValidationUtils.runJavascript(element);
          if (element.Visible === false) break;
          SectionHtmlString.push(
            <DropdownComponent key={"DropdownComponent_nocard_" + element.Id}
              field={element} editable={true}
              title={this.props.isEng ? element.ENUTitle : element.FRATitle}
              labelControlArray={this.props.isEng ? element.ENUTitle : element.FRATitle}
              requiredField={element.Optional !== "Y"}
              isEng={this.props.isEng}
              usedForFilter={false}
              callbackElement={(container) => { this.props.callback(container); }} />
          );
          break;
      }
    })
  }

  private processSubsectionListInCard(subSection: FormSubSections, duplicateSubsecIndex: number, programContainer: ProgramContainer, sumStyle: string, SectionHtmlString: any[], numberOfDuplicates: number) {
    let fieldHtml = [];
    let subsectionTitle = this.props.isEng ? subSection.ENUTitle : subSection.FRATitle;
    let removeButton = [];
    let plateImage = [];
    let removePlatePopUpHTML = [];
    let subsecToSummarize: FormSubSections;

    if (Utils.isNotNullOrEmpty(subSection.SummarizeDuplicateSubsection)) {
     if (numberOfDuplicates >= 1) {
       subsectionTitle = subsectionTitle + " " + (duplicateSubsecIndex + 1).toString();
     }
      subsecToSummarize = this.findDuplicateSubsectionToSummarize(subSection, duplicateSubsecIndex);
      const displayPlateImage = subsecToSummarize.ListOfFormSectionElements.findIndex(ele => ele.Datatype == "CheckPlateAndRenderImage") != -1;
      
      // display plate image for PLP/GLP
      if (displayPlateImage) {
        // type of plate
        let plateTypeLov: LOVValues;
        const plateType = findSectionElementByFieldName(subsecToSummarize.ListOfFormSectionElements, 'plateType');
        if (plateType.Value) {
          plateTypeLov = plateType.ListOfLOVValues.find(l => l.code == plateType.Value);
        }

        // slogan language
        const sloganLanguage = findSectionElementByFieldName(subsecToSummarize.ListOfFormSectionElements, 'sloganLanguage').Value || ENGLISH_SLOGAN;

        // graphic selection
        let wantGraphic = findSectionElementByFieldName(subsecToSummarize.ListOfFormSectionElements, 'wantGraphic')?.Value || NO_GRAPHIC;
        let graphicType = NO_GRAPHIC;
        if (wantGraphic === GRAPHIC) {
          graphicType = findSectionElementByFieldName(subsecToSummarize.ListOfFormSectionElements, 'graphicPlateSearch')?.Value || findSectionElementByFieldName(subsecToSummarize.ListOfFormSectionElements, 'graphicType')?.Value;
        }

        // plate characters
        const plateNumber = findSectionElementByFieldName(subsecToSummarize.ListOfFormSectionElements, 'plateCharacters')?.Value || "";
        // plate count
        const plateCount = findSectionElementByFieldName(subsecToSummarize.ListOfFormSectionElements, 'plateCount')?.Value || null;

        // Update subsectionTitle to graphic Name for GLP only
        if (plateCount && graphicType && graphicType !== NO_GRAPHIC) {  // Only GLP has plateCount
          let graphicTypeDesc = NO_GRAPHIC;
          const graphicElement =
            findSectionElementByFieldName(subsecToSummarize.ListOfFormSectionElements, 'graphicPlateSearch')
            || findSectionElementByFieldName(subsecToSummarize.ListOfFormSectionElements, 'graphicType');

          if (graphicElement?.Value) {
            const graphicTypeLov = graphicElement.ListOfLOVValues.find(l => l.code === graphicElement.Value);
            graphicTypeDesc = (this.props.isEng && graphicTypeLov ? graphicTypeLov.description : graphicTypeLov.FRADescription) || NO_GRAPHIC;
          }   

          subsectionTitle = graphicTypeDesc;
        }
        plateImage.push(
          <div id={`plate-image-summary-` + duplicateSubsecIndex}></div>
        );
        renderPlateImage(plateTypeLov?.code, sloganLanguage, graphicType, plateNumber, duplicateSubsecIndex);
      }
    }

    for (let field of subSection.ListOfFormSectionElements) {
      field.findFieldByName = (fieldName) => {
        const root = subSection.parent.parent;

        const formList = root.ListOfFormSections;
        let eleReturned;
        try {
          formList.forEach(section => {
            if (section.Visible == false) return;
            section.ListOfFormSubSections.forEach(sub => {
              const elements = sub.ListOfFormSectionElements;
              elements.forEach(ele => {
                if (ele.LoadBindName == fieldName) {
                  eleReturned = ele;
                  throw new Error('Exit all loops');
                }
              })
            })
          });
        } catch {
          return eleReturned;
        }
        return null;
      }

      field.findSectionByTitle = (sectionTitle) => {
        const root = subSection.parent.parent;
        const formList = root.ListOfFormSections as FormSections[];
        for (let form of formList) {
          if (form.ENUTitle == sectionTitle) {
            return form;
          }
        }

        return null;
      }
      field.formatField = formatField;

      if (field.Datatype == "SubsectionTextDisplay" && Utils.isNotNullOrEmpty(subSection.SummarizeDuplicateSubsection)) {
        if (field.Script && Utils.isNotNullOrEmpty(field.Script)) {
          const subsecToSummarize = this.findDuplicateSubsectionToSummarize(subSection, duplicateSubsecIndex);
          const customJSFunc = eval(`(${field.Script})`);
          try {
            customJSFunc(field, SCRIPT_ENV, subsecToSummarize, this.props.isEng);
          } catch (ex) {
            console.log(ex);
          }
        }
        if (!field.Visible) continue;
        fieldHtml.push(
            <InfoElementComponent
              isEng={this.props.isEng}
              field={field}
              key={"infoelment_" + field.Id}
              style={this.props.style}
              withButton={false}
              callback={(programContainer) => this.props.callback(programContainer)}>
            </InfoElementComponent>);
      }
      else if (field.Datatype == "EditSubsectionButton" && Utils.isNotNullOrEmpty(subSection.SummarizeDuplicateSubsection)) {
        fieldHtml.push(<EditSubsectionSummaryComponent 
          isEng={this.props.isEng} 
          field={field}
          subsecToSummarize={subsecToSummarize} 
          numberOfDuplicates={numberOfDuplicates} 
          duplicateSubsecIndex={duplicateSubsecIndex} 
          callbackStepIndex={(stepIndex) => this.props.callbackStepIndex(stepIndex)}
          />);

      }
      else if (field.Datatype == "DeleteSubsectionButton" && Utils.isNotNullOrEmpty(subSection.SummarizeDuplicateSubsection)) {
        removeButton.push(
          <button className="ontario-button ontario-button--tertiary"
            value={duplicateSubsecIndex}
            onClick={(e) => {
              this.setState({ removePlatePopUp: e.currentTarget.value as unknown as number });
            }}>
            {this.props.isEng ? field.ENUTitle : field.FRATitle}
          </button>
        );
        const HtmlToReactParser = require('html-to-react').Parser;
        const htmlToReactParser = new HtmlToReactParser(); 
        let popUpBodyMsg=this.props.isEng ? field.callOutBoxBody : field.callOutBoxBodyFRA;
        if(/<[a-z][\s\S]*>/i.test(popUpBodyMsg)){
          popUpBodyMsg=htmlToReactParser.parse(popUpBodyMsg);
        }
        removePlatePopUpHTML.push(
          <ModalBackground tabIndex={-1} aria-labelledby='remove-plate-title' aria-describedby='remove-plate-desc'
            role="alertdialog" aria-modal="true">
            <ModalBody>
              <Heading4 id='remove-plate-title'>{this.props.isEng ? field.callOutBoxTitle : field.callOutBoxTitleFRA}</Heading4>
              <p id='remove-plate-desc' aria-live='assertive'>{popUpBodyMsg}</p>
              <ModalFooter>
                <button className="ontario-button ontario-button--primary"
                  onClick={() => {
                    let indexToRemove = subsecToSummarize.parent.ListOfFormSubSections.findIndex(s => s == subsecToSummarize);

                    if (this.state.removePlatePopUp == 0) {
                      subsecToSummarize.parent.ListOfFormSubSections.splice(indexToRemove, 1);
                      subsecToSummarize.parent.ListOfFormSubSections[indexToRemove].IsDuplicate = false;
                    } else {
                      subsecToSummarize.parent.ListOfFormSubSections.splice(indexToRemove, 1);
                    }
                    this.setState({ removePlatePopUp: -1 });
                  }}>
                  {this.props.isEng ? field.ENUTitle : field.FRATitle}
                </button>
                <button className="ontario-button ontario-button--tertiary"
                  onClick={() => this.setState({ removePlatePopUp: -1 })}>Cancel
                </button>
              </ModalFooter>
            </ModalBody>
          </ModalBackground>);

      }
      else if (field.Datatype === "SummaryList") {
        fieldHtml.push(
          <SummaryListComponent
            key={`summary_list_${field.Id}`}
            field={field}
            isEng={this.props.isEng}
            callbackStepIndex={(index, returnIndex) => this.props.callbackStepIndex(index)}
            callback={(programContainer) => this.props.callback(programContainer)}
          />
        );
      }
      else if (field.Datatype === "PagedSummaryList") {
        fieldHtml.push(
          <PagedSummaryListComponent
            key={`paged_summary_list_${field.Id}`}
            field={field}
            isEng={this.props.isEng}
            callbackStepIndex={(index, returnIndex) => this.props.callbackStepIndex(index)}
            callback={(programContainer) => this.props.callback(programContainer)}
          />
        );
      }
      else if (field.Datatype === "IncidentReportConfirmation") {
        // Render IncidentReportConfirmation callout directly, not in a card
        field.Visible = true;
        field.InvisibleByDefault = false;
        SectionHtmlString.push(
          <IncidentReportConfirmationComponent
            key={`incident_report_confirmation_${field.Id}`}
            field={field}
            programContainer={programContainer}
            isEng={this.props.isEng}
            callbackElement={(container) => this.props.callback(container)}
          />
        );
        continue; // Skip adding to fieldHtml since it's rendered directly
      }
      else if (field.Datatype === "SuccessAlert") {
        // Render SuccessAlert directly outside the card
        const bodyMsgS = this.props.isEng ? field.callOutBoxBody : field.callOutBoxBodyFRA;
        SectionHtmlString.push(
          <FadeableWrapper key={`success_alert_${field.Id}`} field={field} onFadeComplete={() => this.props.callback(getRootComponent(field))}>
            <SuccessAlertComponent
              field={field}
              isEng={this.props.isEng}
              Visible={field.Visible !== false}
              bodyMsg={bodyMsgS}
              title={this.props.isEng ? field.MessageTitle : field.MessageTitleFRA}
            />
          </FadeableWrapper>
        );
        continue; // Skip adding to fieldHtml since it's rendered directly
      }
      else if (field.Datatype === "ErrorAlert") {
        // Render ErrorAlert directly outside the card
        const bodyMsgE = this.props.isEng ? field.callOutBoxBody : field.callOutBoxBodyFRA;
        SectionHtmlString.push(
          <FadeableWrapper key={`error_alert_${field.Id}`} field={field} onFadeComplete={() => this.props.callback(getRootComponent(field))}>
            <ErrorAlertComponent
              field={field}
              isEng={this.props.isEng}
              Visible={field.Visible !== false}
              bodyMsg={bodyMsgE}
              title={this.props.isEng ? field.MessageTitle : field.MessageTitleFRA}
            />
          </FadeableWrapper>
        );
        continue; // Skip adding to fieldHtml since it's rendered directly
      }
      else if (field.Datatype === "WarningAlert") {
        // Render WarningAlert directly outside the card
        const bodyMsgW = this.props.isEng ? field.callOutBoxBody : field.callOutBoxBodyFRA;
        SectionHtmlString.push(
          <FadeableWrapper key={`warning_alert_${field.Id}`} field={field} onFadeComplete={() => this.props.callback(getRootComponent(field))}>
            <WarningAlertComponent
              isEng={this.props.isEng}
              Visible={field.Visible !== false}
              bodyMsg={bodyMsgW}
              title={this.props.isEng ? field.MessageTitle : field.MessageTitleFRA}
            />
          </FadeableWrapper>
        );
        continue; // Skip adding to fieldHtml since it's rendered directly
      }
      else if (field.Datatype === 'Modal') {
        SectionHtmlString.push(
          <FieldModalComponent field={field}
            isEng={this.props.isEng}
            programContainer={this.props.programContainer}
            callback={(programContainer) => this.props.callback(programContainer)}
            onButtonClick={async (_data) => {
              const triggerButton = (field as any)._triggerButton as FormSectionElements;
              const success = triggerButton ? await this.callFieldApi(triggerButton, _data) : true;
              if (!success) return;
              const successMessage = this.props.isEng
                ? ((triggerButton as any)?.SuccessMessage || 'Operation completed successfully.')
                : ((triggerButton as any)?.SuccessMessageFRA || (triggerButton as any)?.SuccessMessage || 'Opération terminée avec succès.');
              if ((triggerButton as any)?.StayOnPage) {
                field.Value = null;
                (this.props.programContainer as any)._withdrawnLocally = true;
                this.setState({ successBannerMessage: successMessage });
                this.props.callback(this.props.programContainer);
                return;
              }
              const status = field.Value;
              field.Value = null;
              this.props.setStateProp(true, false, '', false, false, status || '', status ||'');
              this.setState({ confirmationVisible: true, summaryVisible: false });
              this.props.callback(this.props.programContainer);
            }}
          />
        );
      }
      else if (field.Datatype !== "Button" && field.Datatype != 'Label' && field.Datatype !== 'HiddenField') {
        FormValidationUtils.runJavascript(field);
        if (!field.Visible) continue;
        if (this.props.style === 'Table') {
          sumStyle = 'Table';
          fieldHtml.push(
            <TableSummaryComponent
              isEng={this.props.isEng}
              field={field}
              key={"table_sum" + field.Id}
              withButton={field.Datatype != "Label"}
              handleEditButtonClick={this.handleEditButtonClick}
              callback={(programContainer) => this.props.callback(programContainer)}
              hideChangeLink={subSection.parent?.CustomizedSummaryProps?.changeLinkInSubsectionLevel || subSection.parent?.CustomizedSummaryProps?.hideAllChangeLinks}
            />
          )
        } else {
          fieldHtml.push(
            <InfoElementComponent
              isEng={this.props.isEng}
              field={field}
              key={"infoelment_" + field.Id}
              style={this.props.style}
              withButton={field.Datatype != "Label"}
              handleEditButtonClick={this.handleEditButtonClick}
              callback={(programContainer) => this.props.callback(programContainer)}>
            </InfoElementComponent>
          );
        }
      } else {
        if (field.Datatype == "Button"
          && (!this.props.style || this.props?.style === 'Button' || this.props?.style === 'All' || this.props?.style === 'Table')) {
          FormValidationUtils.runInitJavascript(field);
          if (field.Visible === false) continue;
          let arrayToString = (value: string[]) => Array.isArray(value)
            ? value.join(' ') : (value ? String(value) : null);
          let ClassName = "ontario-button " + (arrayToString(field.ClassName) ?? "ontario-button--secondary");
          fieldHtml.push(
            <button
              className={ClassName}
              key={"elemnt_inf_button" + field.Id}
              onClick={() => this.handleEditButtonClick(field)}
            >
              {this.props.isEng ? field.ENUTitle : field.FRATitle}
            </button>
          )
        }
        if (field.Datatype == 'Label') {
          fieldHtml.push(<RichTextComponent field={field}
            editable={true}
            title={this.props.isEng ? field.ENUTitle : field.FRATitle}
            labelControlArray={this.props.isEng ? field.ENUTitle : field.FRATitle}
            requiredField={false}
            callbackElement={null}
            isEng={this.props.isEng}></RichTextComponent>);
        }
      }
    }
    // Only render Card if there's content to display (skip if fieldHtml is empty and no other content)
    const hasCardContent = fieldHtml.length > 0 || plateImage.length > 0 || (numberOfDuplicates > 0 && removeButton.length > 0);

    if (this.props.style !== 'Default' && hasCardContent) {
      const sectionId = subSection.parent?.Id ?? 's';
      const cardId = `subsection-title-${sectionId}-${subSection.Sequence}-${duplicateSubsecIndex}`;
      if (subSection.parent?.CustomizedSummaryProps?.changeLinkInSubsectionLevel) {
        if (this.shouldShowSubSection(subSection)) {
          subSection.SectionId = duplicateSubsecIndex.toString();  // ChangeLinkScripts can use this id to determine the elements to hide/show.
          const changeLinkLabel = {
            eng: subSection.ChangeLinkProps?.changeLinkLabel ? subSection.ChangeLinkProps?.changeLinkLabel : 'Change',
            fr: subSection.ChangeLinkProps?.changeLinkLabelFRA ? subSection.ChangeLinkProps?.changeLinkLabelFRA : 'Modifier'
          };
          const changeLinkElement = !subSection.ReadOnlyElements && !subSection.HideChangeLink &&
                <button id={`subsection-update-btn-${sectionId}-${subSection.Sequence}-${duplicateSubsecIndex}`}
                  style={{display: 'flex', alignItems: 'start', border: 'none', backgroundColor: '#fff', cursor: 'pointer', textDecoration: 'underline', padding: '0.25rem 0'}}
                  title={this.props.isEng ? `Edit ${subsectionTitle}` : `Modifier ${subsectionTitle}`}
                  onClick={() => {
                      this.handleSubsectionEditButtonClick(subSection);
                  }}>
                  <span style={{color: "#00478f", fontSize: "1.2rem"}}>{this.props.isEng ? changeLinkLabel.eng : changeLinkLabel.fr}</span>
                </button>;
          const hasTitle = !!subsectionTitle;
          SectionHtmlString.push(
            <Card
              key={cardId}
              id={cardId}
              className={`inforCard ${this.props.style === 'Table' ? 'notBordered' : ''}`}
              title={subsectionTitle}
              link={hasTitle ? changeLinkElement : undefined}
              content={
                <>
                  {!hasTitle && changeLinkElement && (
                    <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '0.5rem' }}>
                      {changeLinkElement}
                    </div>
                  )}
                  {plateImage}
                  {fieldHtml}
                  {numberOfDuplicates > 0 && removeButton}
                  {this.state.removePlatePopUp == duplicateSubsecIndex && removePlatePopUpHTML}
                </>
              }>
            </Card>
          );
        }
      } else {
        SectionHtmlString.push(
          <Card
            key={cardId}
            id={cardId}
            className={`inforCard ${sumStyle ? 'notBordered' : ''}`}
            title={subsectionTitle}
            content={
              <>
                {plateImage}
                {fieldHtml}
                {numberOfDuplicates > 0 && removeButton}
                {this.state.removePlatePopUp == duplicateSubsecIndex && removePlatePopUpHTML}
              </>
            }>
          </Card>
        );
      }
    }

    if (Utils.isNotNullOrEmpty(subSection.SummarizeDuplicateSubsection)) duplicateSubsecIndex = duplicateSubsecIndex + 1;
  }

  private findDuplicateSubsectionToSummarize(activeSubsec: FormSubSections, index: number) {
    if (Utils.isNullOrEmpty(activeSubsec.SummarizeDuplicateSubsection)) return null;
    for (const section of this.props.programContainer.ListOfFormSections) {
      let duplicateSubs = section.ListOfFormSubSections.filter(sub => sub.ENUTitle == activeSubsec.SummarizeDuplicateSubsection);
      if (index < duplicateSubs.length) return duplicateSubs[index];
    }
    return null;
  } 

  private shouldShowSubSection(subSection: FormSubSections) {
    	if (subSection.CanBeDuplicate && subSection.SummarizeDuplicateSubsection) {
        const sectionElementsFound = subSection.ListOfFormSectionElements.filter((sectionElement) => sectionElement.Value);
        return (sectionElementsFound && sectionElementsFound.length);
      }
      return true;
  }

  private processDuplicates(subsectionListInCard: FormSubSections[], programContainer: ProgramContainer) {
    let numberOfDuplicates = 0;
    for (let i = subsectionListInCard.length - 1; i >= 0; i--) {
      let currentSubsec = subsectionListInCard[i];
      if (currentSubsec.CanBeDuplicate && currentSubsec.SummarizeDuplicateSubsection) {
        const sectionIndex = programContainer.ListOfFormSections.findIndex(section => section.findSubsectionByTitle(currentSubsec.SummarizeDuplicateSubsection));
        const section = programContainer.ListOfFormSections[sectionIndex];
        section.ListOfFormSubSections.forEach(s => {
          if (s.IsDuplicate) {
            let shallowCopySubsec = { ...currentSubsec } as FormSubSections;
            shallowCopySubsec.IsDuplicate = true;
            shallowCopySubsec.ListOfFormSectionElements = []
            currentSubsec.ListOfFormSectionElements.forEach(originalEle => {
              let eleShallowCopy = { ...originalEle } as FormSectionElements;
              eleShallowCopy.parent = shallowCopySubsec;
              shallowCopySubsec.ListOfFormSectionElements.push(eleShallowCopy);
            });
            subsectionListInCard.splice(i + 1, 0, shallowCopySubsec);
            numberOfDuplicates = numberOfDuplicates + 1;
          }
        });
      }
    }
    return numberOfDuplicates;
  }

  generateConfirmationHtmlString = () => {
    const confirmationComponentName = this.props.programContainer.CategoryId.toUpperCase() + 'Confirmation';

    const props = {
      programContainer: this.props.programContainer,
      isEng: this.props.isEng,
      transactionId: this.props.transactionId,
      Visible: this.props.confirmationVisible,
      uploadReceiptSuccess: this.state.uploadReceiptSuccess,
      showDownloadSummary: this.props.showDownloadSummary,
      applicationType: this.props.applicationType,
      renewType: this.props.renewType,
      renewalResponse: this.state.renewalResponse,
      captchaToken: this.state.captchaToken
    }

    let SectionHtmlString = [];

    if (this.props.programContainer.CategoryId.toUpperCase() == 'CHC' || this.props.programContainer.CategoryId.toUpperCase() == 'APP'
      || this.props.programContainer.CategoryId.toUpperCase() === 'ODR' || this.props.programContainer.CategoryId.toUpperCase() === 'VECON'
      || this.props.programContainer.CategoryId.toUpperCase() === 'ADMIN-PORTAL') {
      SectionHtmlString.push(
        <DynamicComponentRenderer componentName={confirmationComponentName} componentProps={props} onButtonClick={(p) => { }} callback={(p) => this.props.callback(p)} />
      );
    } else {
      SectionHtmlString.push(this.state.confirmationVisible && this.generateGeneralConfirmationPage());
    }

    return SectionHtmlString;
  }

  handleBackButtonClick = (backToUrl) => {
    sessionStorage.removeItem(USER_PROFILE);
    const url = getEnvVar('PUBLIC_CXP_WEB_BASE_PATH') + '/auth/signin?redirectUrl=' + backToUrl;
    window.location.assign(url);
  }

  generateGeneralConfirmationPage() {
    let SectionHtmlString = [];
    const confirmationPageArray = this.props.programContainer.ListOfFormSections.filter(p => p.IsConfirmationPage);

    if (confirmationPageArray.length == 0) return null;

    const confirmationPage = confirmationPageArray[0];
    const hasUserProfile = !!this.props.programContainer.Profile?.userId || !!this.props.programContainer.Profile?.email;

    let backToLabel, backToUrl;
    if (hasUserProfile) {
      backToLabel = this.props.isEng ? "Back to home" : "Retour à l'accueil";
      backToUrl = getEnvVar('PUBLIC_CXP_WEB_BASE_PATH') + '/auth/signin?redirectUri=/web' + (this.props.isEng ? '' : '/fr') + '/dashboard';
    } else {
      backToLabel = this.props.isEng ? "Back to Ontario.ca" : "Revenir à Ontario.ca";
      backToUrl = this.props.isEng ? "https://www.ontario.ca/page/government-ontario" : "https://www.ontario.ca/fr/page/gouvernement-de-lontario";
    }

    if (hasUserProfile) {
      SectionHtmlString.push(
        <StepIndicator withbackbtn={true} title={backToLabel}
          sectionSequence={0} section={confirmationPage} isEng={this.props.isEng}
          customOnclick={() => this.handleBackButtonClick(backToUrl)}
          callback={(index)=>this.props.callbackStepIndex(index)}
        />
      );
    } else {
      SectionHtmlString.push(
        <StepIndicator withbackbtn={true} title={backToLabel}
          sectionSequence={0} section={confirmationPage} isEng={this.props.isEng}
        // customOnclick={() => window.location.replace(backToUrl)} 
         callback={(index)=>{
          if(this.props.programContainer.ListOfFormSections[index].CustomizedReviewPage){
            // this.props.callbackStepIndex(index)
            this.setState({ confirmationVisible:false, summaryVisible:true  }); 
          }
          // this.props.callbackStepIndex(index)
         }}
        />
      );
    }
    // SectionHtmlString.push(
    //   <StepIndicator withbackbtn={true} title='Back' sectionSequence={0} isEng={this.props.isEng} />
    // );

    for (let subSection of confirmationPage.ListOfFormSubSections) {
      SectionHtmlString.push(<SubSectionComponent key={'sub_' + subSection.Id + subSection.Id}
        isEng={this.props.isEng}
        activeSubSection={subSection}
        editable={false}
        callback={(programContainer) => {
          this.props.callback(programContainer)
        }} />)
    }

    return SectionHtmlString;
  }

  private handleConfirmApiAction = async () => {
    const { pendingApiAction } = this.state;
    if (!pendingApiAction) return;

    const { method, url, headers, field } = pendingApiAction;
    this.setState({ pendingApiAction: null });

    try {
      await axios({ method, url, headers });
      const successMessage = this.props.isEng
        ? ((field as any).SuccessMessage || 'Operation completed successfully.')
        : ((field as any).SuccessMessageFRA || (field as any).SuccessMessage || 'Opération terminée avec succès.');
      alert(successMessage);
      window.location.reload();
    } catch (error) {
      console.error('API call failed:', error);
      alert('Operation failed: ' + (error as Error).message);
    }
  };

  private renderConfirmActionModal() {
    const { pendingApiAction } = this.state;
    if (!pendingApiAction) return null;

    const { field } = pendingApiAction;
    const title = this.props.isEng
      ? ((field as any).ConfirmTitle || (field as any).ENUTitle + ' ?')
      : ((field as any).ConfirmTitleFRA || (field as any).FRATitle + ' ?');
    const message = this.props.isEng
      ? ((field as any).ConfirmMessage || 'Are you sure you want to proceed?')
      : ((field as any).ConfirmMessageFRA || (field as any).ConfirmMessage || 'Êtes-vous sûr de vouloir continuer?');
    const confirmLabel = this.props.isEng
      ? ((field as any).ConfirmButtonLabel || 'Yes, proceed')
      : ((field as any).ConfirmButtonLabelFRA || (field as any).ConfirmButtonLabel || 'Oui, continuer');
    const cancelLabel = this.props.isEng ? 'Cancel' : 'Annuler';

    return (
      <>
        <ModalOverlay onClick={() => this.setState({ pendingApiAction: null })} />
        <ModalBackground tabIndex={-1} aria-labelledby='confirm-action-title' aria-describedby='confirm-action-desc'
          role="alertdialog" aria-modal="true">
          <ModalBody>
            <Heading4 id='confirm-action-title'>{title}</Heading4>
            <p id='confirm-action-desc' aria-live='assertive'>{message}</p>
            <ModalFooter>
              <button className="ontario-button ontario-button--secondary"
                onClick={this.handleConfirmApiAction}>
                {confirmLabel}
              </button>
              <button className="ontario-button ontario-button--primary"
                onClick={() => this.setState({ pendingApiAction: null })}>
                {cancelLabel}
              </button>
            </ModalFooter>
          </ModalBody>
        </ModalBackground>
      </>
    );
  }

  render() {

    let SectionHtmlString = [];

    if (this.state.successBannerMessage) {
      SectionHtmlString.push(
        <div className="ontario-alert ontario-alert--success callout-max-width" role="alert" key="success-banner">
          <div className="ontario-alert__header" style={{ display: 'flex', alignItems: 'center' }}>
            <div className="ontario-alert__header-icon">
              <svg className="ontario-icon" aria-hidden="true" focusable="false" viewBox="0 0 24 24" preserveAspectRatio="xMidYMid meet">
                <use href="#ontario-icon-alert-success"></use>
              </svg>
            </div>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <h2 className="ontario-alert__header-title ontario-h4">{this.state.successBannerMessage}</h2>
            </div>
          </div>
        </div>
      );
    }

    SectionHtmlString.push(this.generateSummaryHtmlString());

    SectionHtmlString.push(this.generateConfirmationHtmlString());
    
    if (this.props.submissionError) {
      const techSection = FormValidationUtils.getTechSection(this.props.programContainer);
      if (techSection) {
        techSection.Visible = true;
      }
      const index = this.props.programContainer.ListOfFormSections.findIndex(p => p.IsTechErrorPage);
      this.props.callbackStepIndex(index);
    }

    SectionHtmlString.push(<SpinnerComponent isBusy={this.state.isBusy} />);

    SectionHtmlString.push(this.renderConfirmActionModal());

    return SectionHtmlString;
  }
}
